import React, {useCallback, useMemo} from 'react';
import {useIntl} from 'react-intl';
import cn from 'classnames';

import {useSetUI} from 'coreHooks';
import {useCategory} from 'coreHooks/categories';
import {EOncloseActions} from 'coreStore/data/ui/types';
import {useRouterHistory as useHistory, useRouterLocation as useLocation} from 'coreUtils/adapters/router';

import Button from 'brandComponents/Button';

import {ETabType} from '../../ISizeGuideFlyout';

import {ISizeGuideButtonProps} from './ISizeGuideButton';
import messages from './messages';

import style from './SizeGuideButton.module.scss';

const SizeGuideButton = ({product, renderSize = true, renderItem = false, closeOnBack}: ISizeGuideButtonProps) => {
	const intl = useIntl();
	const setUI = useSetUI();
	const history = useHistory();
	const location = useLocation();
	const primaryCategory = useCategory(product.getPrimaryCategoryId());
	const sizeGuideId = useMemo(
		() => product
			? product.getSizeGuideId() || primaryCategory?.getSizeGuideIds()
			: null,
		[product]
	);

	const fitInformation = useMemo(() => product?.getFitInformation(), [product]);

	const openSizeGuideFlyout = useCallback((selectedTab: ETabType) => {
		setUI.hideSizeSelector();
		setUI.showSizeGuide(
			sizeGuideId,
			product?.getItemMeasurements(),
			closeOnBack ?
				EOncloseActions.RESTORE_SIZE_SELECTOR_AND_CLOSE_ON_BACK :
				EOncloseActions.RESTORE_SIZE_SELECTOR,
			selectedTab,
			product?.getProductId(),
			product.getFitInformation(),
			primaryCategory?.getName(),
		);

		if (closeOnBack) {
			history.push(`${location.pathname}${location.search}`);

			window.onpopstate = () => {
				setUI.hideSizeGuide();
			};
		}
	}, [product]);

	if (!product || !renderSize) {
		return null;
	}

	return renderItem ? (
		<>
			{(sizeGuideId || fitInformation) &&
				<Button
					classNames={cn(style['size-guide-button__size-guide'])}
					onClick={() => openSizeGuideFlyout(ETabType.SIZE)}
					variant="tertiary"
				>
					<span>{intl.formatMessage(messages.sizeGuide)}</span>
				</Button>
			}
		</>
	) : (
		<>
			{(sizeGuideId) &&
				<Button
					classNames={cn(style['size-guide-button__size-guide__only-fit'])}
					variant="tertiary"
					onClick={() => openSizeGuideFlyout(ETabType.SIZE)}
				>
					<span>{intl.formatMessage(messages.sizeGuide)}</span>
				</Button>
			}
		</>
	);
};

export default SizeGuideButton;
